// The valley in the low-poly look, with the Wanderer (phase A3 joined to A2): @rpg/look's `valley-day` look on the
// main scene at the boot's tier; the course's meshes re-made as palette-coloured facets on the look's one surface
// material; the hero's capsule placeholder hidden and the Wanderer built from its card in its place, driven by the
// controller's state through the figure's `Locomotion` seam, its feet probing the valley's collision world.
//
// The adapter (controller state → figure): each sim step records the hero's feet and facing; each frame poses the
// figure's root between the last two steps at the frame's alpha (the camera samples its target the same way), then
// steps the motion: speed and velocity from the controller, grounded on the ground, a jump while rising from one, a
// glide in the glide mode; on the mount it stands (speed 0) on the seat.
// With the sky (phase C joined, the default scene): the sky's view (src/sky/view.ts) is made before the look (its dome,
// and on high its shaft pass, go in as the look's options), attached after it with the shaft spots, applied every frame
// after the hero's pose, and warmed with the rest (its shaft cards drawn and its rain falling during the warm-up).
// The hero pushes walk-through plants aside as it passes (the foliage push, decision 0085): its posed feet are the
// look's pusher each frame, with the tuning table's `world.foliage.*` values (read each frame, so live tuning takes at
// once), the wobble scaled by its ground speed.
// A view: nothing here runs in the sim phase but the record of the hero's state.
import * as THREE from "three";
import { applyPoses, buildFigure, createMotion, resetTurns, type Figure, type FigureCard, type Locomotion, type Motion, type PoseSample } from "@rpg/figure";
import { createLowPolyLook, setFoliagePush, setPushers, type FoliagePush, type LowPolyLook, type LowPolySceneLook, type LowPolyTier, type ShaftSpot } from "@rpg/look";
import { facet, seeded } from "@rpg/meshkit";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { ValleyRig } from "./scene";
import { CAMERA_LAYERS } from "./camera";
import type { SkyView } from "../sky/view";

/** The foliage push's values, each a `world.foliage.<key>` tuning row. */
const PUSH_KEYS = ["radius", "height", "strength", "dip", "wobble", "rate"] as const;

/** The valley's dressed view. */
export interface ValleyLook {
  look: LowPolyLook;
  hero: { figure: Figure; motion: Motion };
  /** Draw a frame through the look (its post chain on high). */
  render(): void;
  /** A summary for `state()`. */
  state(): { tier: string; motion: string; drift: number };
}

export function mountValleyLook(o: {
  runtime: Runtime;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  content: SpikeContent;
  rig: () => ValleyRig;
  /** "low" or "high" (the boot's pick, or the settings' choice at boot). */
  tier: string;
  /** The sim view's root (its instanced meshes are re-made in the look). */
  view: THREE.Object3D;
  /** Whether the hero has its arms out (a weapon drawn: the dojo, phase B). Default never. */
  acting?: () => boolean;
  /** The hero's action poses and hit reactions at this frame (the bodies module's pose track sampled at the frame's
   *  alpha), laid over the motion; null or absent: none. */
  poses?: (alpha: number) => PoseSample | null;
  /** Called with the look and the hero before the look's warm-up render (a scene mounts its view parts here: the
   *  fight's figures, the impact director), its result after it (to clear what it showed for the warm-up). */
  warm?: (look: LowPolyLook, hero: { figure: Figure; motion: Motion }) => (() => void) | void;
  /** The time of day and the weather on screen (phase C): the sky's view and where its light shafts stand. */
  sky?: { view: SkyView; shafts: readonly ShaftSpot[] };
}): ValleyLook {
  const reg = o.content.registry;
  const sceneLook = reg.get("looks", "valley-day") as unknown as LowPolySceneLook | undefined;
  if (!sceneLook) throw new Error("valley look: no look \"valley-day\"");
  const tierId = o.tier === "low" ? "low" : "high";
  const tier = reg.get("tiers", tierId) as unknown as LowPolyTier | undefined;
  if (!tier) throw new Error(`valley look: no tier "${tierId}"`);

  // the look brings its own sun, fill and sky: the placeholder lights and background go
  const old: THREE.Object3D[] = [];
  o.scene.traverse((c) => (c as THREE.Light).isLight && old.push(c));
  for (const l of old) l.removeFromParent();
  o.scene.background = null;
  const look = createLowPolyLook({ renderer: o.renderer, scene: o.scene, camera: o.camera, look: sceneLook, tier, shadowExtent: 18, ...(o.sky ? o.sky.view.lookOptions : {}) });
  if (o.sky) o.sky.view.attach(look, o.sky.shafts);
  const tuning = (id: string): number => {
    const v = (reg.get("tuning", id) as { value?: number } | undefined)?.value;
    if (typeof v !== "number") throw new Error(`valley look: no tuning "${id}"`);
    return v;
  };
  const push: FoliagePush = { radius: 0, height: 0, strength: 0, dip: 0, wobble: 0, rate: 0 };
  /** The push's values now, the wobble times `moving` (0 to 1). */
  const pushWith = (moving: number): FoliagePush => {
    for (const k of PUSH_KEYS) push[k] = tuning(`world.foliage.${k}`);
    push.wobble *= moving;
    return push;
  };
  setFoliagePush(look.uniforms, pushWith(0));

  // the course: each of the sim view's meshes faceted in its colour on the one surface material (the hero's capsule
  // hidden: the Wanderer stands there)
  const rng = seeded(0x7a11e);
  o.view.traverse((c) => {
    const m = c as THREE.Mesh;
    if (!m.isMesh || m.material === look.surface) return;
    const mat = m.material as THREE.MeshLambertMaterial;
    if (mat.transparent) return; // the pond's sheet stays a sheet
    if (m.name === "body:hero") {
      m.visible = false;
      return;
    }
    const big = (m.geometry.attributes.position?.count ?? 0) > 1000;
    m.geometry = facet(m.geometry, { colour: mat.color.clone(), jitter: 0, tint: big ? 0.05 : 0.08, rng });
    m.material = look.surface;
  });
  look.track(o.view);

  // the Wanderer from its card, on the look's material
  const row = reg.get("cards", "wanderer");
  if (!row) throw new Error("valley look: no wanderer card");
  const card = row as unknown as FigureCard;
  const figure = buildFigure(card, { material: look.surface, colour: look.palette });
  figure.root.name = "figure:wanderer";
  o.scene.add(figure.root);
  look.track(figure.root, { blob: (row as { blob?: number }).blob });
  const r = o.rig();
  const probe = (x: number, z: number, fromY: number): number | null =>
    r.collision.castRay({ x, y: fromY, z }, { x: 0, y: -1, z: 0 }, 40, { layers: CAMERA_LAYERS })?.point.y ?? null;
  const motion = createMotion(figure, card.motion!, probe);

  // the adapter: the controller's state per step, the figure posed between steps
  const snap = () => {
    const h = r.hero();
    return { x: h.feet.x, y: h.feet.y, z: h.feet.z, yaw: h.yaw };
  };
  let prev = snap();
  let curr = prev;
  o.runtime.on(
    "sim",
    () => {
      prev = curr;
      curr = snap();
      // a warp or a dismount: no sliding across the jump
      if (Math.hypot(curr.x - prev.x, curr.y - prev.y, curr.z - prev.z) > 2) prev = curr;
    },
    { order: 1e8, name: "valley look: hero state" },
  );
  const locomotion = (): Locomotion => {
    const h = r.hero();
    const riding = h.mode === "mounted";
    const speed = riding ? 0 : Math.hypot(h.vel.x, h.vel.z);
    return {
      speed,
      velocity: riding ? { x: 0, y: 0, z: 0 } : { x: h.vel.x, y: h.vel.y, z: h.vel.z },
      grounded: riding || h.mode === "ground",
      jump: h.mode === "air" && h.vel.y > 0,
      glide: h.mode === "glide",
      acting: o.acting?.() ?? false,
      lookAt: null,
    };
  };
  const lerpYaw = (a: number, b: number, t: number): number => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;
  let last = { x: curr.x, z: curr.z };
  o.runtime.on(
    "late",
    (c) => {
      // a world frozen under the title or a menu holds its pose (and costs nothing: each pose probes the ground)
      if (c.mode === "play" && o.runtime.paused) return;
      const t = c.alpha;
      const x = prev.x + (curr.x - prev.x) * t;
      const z = prev.z + (curr.z - prev.z) * t;
      figure.root.position.set(x, prev.y + (curr.y - prev.y) * t, z);
      figure.root.rotation.y = lerpYaw(prev.yaw, curr.yaw, t);
      if (Math.hypot(x - last.x, z - last.z) > 2) motion.reset();
      last = { x, z };
      // a long frame (a test's advance) poses in steps of at most 1/60 s, so the springs and the gait see real time
      const n = Math.min(90, Math.max(1, Math.ceil(c.dt * 60 - 1e-6)));
      const loco = locomotion();
      // the pose layer (phase B's bodies): the motion's turns start from rest each update, the poses go on after
      const sample = o.poses?.(t) ?? null;
      if (sample) loco.acting = loco.acting || sample.arms;
      for (let i = 0; i < n; i++) {
        if (sample) resetTurns(figure);
        motion.update(c.dt / n, loco);
      }
      if (sample) applyPoses(figure, sample);
      look.focus(figure.root.position);
      // the foliage push: the hero's feet, the wobble only while it moves
      setFoliagePush(look.uniforms, pushWith(Math.min(1, loco.speed / tuning("controller.runSpeed"))));
      setPushers(look.uniforms, [figure.root.position], o.runtime.clock.time);
    },
    { order: -15, name: "valley look: hero pose" },
  );
  // the sky's look for this frame, after the hero's pose and the rooms' light (whose blend it reads)
  const sky = o.sky?.view ?? null;
  if (sky) o.runtime.on("late", (c) => sky.frame(c.dt, o.runtime.clock.time), { order: 10, name: "valley look: the sky" });
  addEventListener("resize", () => look.resize(innerWidth, innerHeight));
  motion.reset();
  const cleared = o.warm?.(look, { figure, motion });
  // every program the day and the weathers use compiles before ready: the shaft cards drawn, the rain falling
  sky?.frame(0, 0);
  const back = sky?.forWarm();
  look.warm();
  back?.();
  cleared?.();

  return {
    look,
    hero: { figure, motion },
    render: () => look.render(),
    state: () => {
      const rep = motion.report();
      return { tier: tierId, motion: rep.state, drift: rep.drift.max };
    },
  };
}
